import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  AppBar,
  Toolbar,
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  IconButton
} from '@mui/material';
import {
  ArrowBack,
  LocalFireDepartment,
  Security,
  Lock,
  Visibility,
  Share,
  Update,
  Email,
} from '@mui/icons-material';

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();

  const sections = [
    {
      title: '1. Introduction',
      icon: <LocalFireDepartment />,
      content: [
        'This Privacy Policy explains what FastTime collects, how we use and share it, how long we retain it, and the choices available to you when you use our mobile app, website, and related services.',
      ],
    },
    {
      title: '2. Information We Collect',
      icon: <Visibility />,
      subsections: [
        {
          subtitle: '2.1 Personal Information',
          items: [
            'Name, email address, account identifier, and securely hashed password',
            'Profile details, goals, preferences, and support communications',
          ],
        },
        {
          subtitle: '2.2 Health and Wellness Data',
          items: [
            'Age, body measurements, sex or gender information you choose to provide',
            'Fasting session data (start time, end time, duration, protocol type)',
            'Daily metrics (water intake, steps, exercise duration, mood)',
            'Apple Health or other connected health metrics you specifically authorize, including activity, heart rate, sleep, blood glucose, blood oxygen, and blood pressure categories',
            'Weight history and progress tracking',
            'Meal logs and calorie intake',
          ],
        },
        {
          subtitle: '2.3 Usage Information',
          items: [
            'Device and app identifiers used for account security and subscription enforcement',
            'Server logs such as IP address, access time, request status, and browser or app version',
            'Purchase and premium-entitlement status',
            'Photos, files, and diagnostics only when you choose food-photo recognition, product submission, or support attachments',
          ],
        },
      ],
    },
    {
      title: '3. How We Use Your Information',
      icon: <Security />,
      content: [
        'We use the collected information to:',
      ],
      items: [
        'Provide, maintain, and improve our services',
        'Track your fasting sessions and health metrics',
        'Calculate and display personalized progress and nutrition features',
        'Send you notifications and reminders (if enabled)',
        'Communicate with you about updates, security alerts, and support',
        'Detect, prevent, and address technical issues and security threats',
        'Verify purchases, prevent abuse, and comply with legal obligations',
      ],
    },
    {
      title: '4. Data Security',
      icon: <Lock />,
      content: [
        'We take the security of your personal information seriously and implement appropriate technical and organizational measures:',
      ],
      items: [
        'All passwords are encrypted using industry-standard bcrypt hashing',
        'Data transmission is secured using HTTPS/SSL encryption',
        'Access to personal data is restricted to authorized personnel only',
        'JWT token-based authentication with short-lived access tokens',
      ],
      note: 'However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your personal information, we cannot guarantee its absolute security.',
    },
    {
      title: '5. Information Sharing and Disclosure',
      icon: <Share />,
      content: [
        'We do not sell personal information and do not track you across other companies\' apps or websites. We share only what is needed to operate a requested feature:',
      ],
      items: [
        'With hosting, database, email, and support providers that operate the FastTime service',
        'With Apple to process and verify in-app purchases',
        'When you actively choose cloud food-photo recognition, with a third-party AI provider such as Groq or Google Gemini; use manual food entry if you do not want to send an image',
        'To comply with legal obligations, court orders, or government requests',
        'To protect our rights, property, or safety, or that of our users',
      ],
    },
    {
      title: '6. Apple Health and Optional Permissions',
      icon: <Security />,
      content: [
        'Apple Health, camera, photos, motion, location, and notifications are optional. FastTime accesses only categories you approve, and you can change access in system settings. Health data is used only for requested health and app functionality, never for advertising, marketing, or data brokerage.',
      ],
    },
    {
      title: '7. Your Rights and Choices',
      icon: <Security />,
      items: [
        'Access or update profile and app information through your account',
        'Disable optional permissions and notifications in device settings',
        'Use manual food entry instead of third-party AI photo recognition',
        'Permanently delete your account in the mobile app under Settings › Delete account',
        'Contact FastTime Support for privacy access or correction requests',
      ],
    },
    {
      title: '8. Retention and Deletion',
      icon: <Update />,
      content: [
        'We retain account information while your account is active and as needed to provide the service. Completing in-app account deletion removes your profile and linked synced health, fasting, nutrition, achievement, support, product-submission, and subscription-status records.',
        'Limited records may be retained only when required for legal, tax, fraud-prevention, security, or dispute obligations. Apple retains App Store transaction records under Apple\'s policies. Removing the app alone does not delete server data or cancel an Apple subscription.',
      ],
    },
    {
      title: '9. Children\'s Privacy',
      icon: <Security />,
      content: [
        'Our service is not intended for children under 13 years of age.',
        'We do not knowingly collect personal information from children under 13.',
        'Fasting may be inappropriate for minors. A parent or guardian and qualified healthcare professional should be consulted before a minor uses fasting guidance.',
      ],
    },
    {
      title: '10. International Data Transfers',
      icon: <Share />,
      content: [
        'Your information may be transferred to and maintained on servers located outside of your state, province, country, or other governmental jurisdiction.',
        'We ensure that appropriate safeguards are in place to protect your information in accordance with this Privacy Policy.',
      ],
    },
    {
      title: '11. Changes to This Privacy Policy',
      icon: <Update />,
      content: [
        'We may update this Privacy Policy from time to time to reflect changes in our practices or for legal, operational, or regulatory reasons.',
        'We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.',
        'We encourage you to review this Privacy Policy periodically for any changes.',
      ],
    },
    {
      title: '12. Contact Us',
      icon: <Email />,
      content: [
        'For privacy questions or requests, use Contact Support while signed in to the FastTime mobile app or web dashboard. We use your account email to verify requests involving account data.',
      ],
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Navigation Bar */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          background: 'rgba(11, 15, 25, 0.8)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ py: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
              <IconButton
                onClick={() => navigate('/')}
                sx={{
                  mr: 2,
                  color: 'text.secondary',
                  '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.05)' }
                }}
              >
                <ArrowBack />
              </IconButton>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocalFireDepartment sx={{ fontSize: 32, color: 'primary.main' }} />
                <Typography variant="h5" component="div" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.5px' }}>
                  FastTime
                </Typography>
              </Box>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Content */}
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 10 } }}>
        <Box
          className="glass-card"
          sx={{
            p: { xs: 4, md: 8 },
            borderRadius: '24px',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 800, mb: 3, color: 'text.primary', letterSpacing: '-1px' }}>
              Privacy Policy
            </Typography>
            <Chip
              label="Last Updated: August 2, 2026"
              sx={{
                mb: 4,
                bgcolor: 'rgba(139, 92, 246, 0.1)',
                color: 'primary.light',
                fontWeight: 600,
                border: '1px solid rgba(139, 92, 246, 0.2)',
                borderRadius: '8px'
              }}
            />
            <Typography variant="h6" sx={{ color: 'text.secondary', maxWidth: 600, mx: 'auto', lineHeight: 1.8, fontWeight: 400 }}>
              Your privacy is fundamentally important to us. This policy outlines how we protect and manage your personal information.
            </Typography>
          </Box>

          <Divider sx={{ mb: 6, borderColor: 'rgba(255,255,255,0.05)' }} />

          {/* Sections */}
          {sections.map((section, index) => (
            <Box key={index} sx={{ mb: 8 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box
                  sx={{
                    color: 'primary.main',
                    bgcolor: 'rgba(139, 92, 246, 0.1)',
                    p: 1.5,
                    borderRadius: '12px',
                    display: 'flex',
                    mr: 2,
                    border: '1px solid rgba(139, 92, 246, 0.2)'
                  }}
                >
                  {section.icon}
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.5px' }}>
                  {section.title}
                </Typography>
              </Box>

              {/* Content paragraphs */}
              {section.content && section.content.map((paragraph, pIndex) => (
                <Typography
                  key={pIndex}
                  variant="body1"
                  paragraph
                  sx={{ lineHeight: 1.9, color: 'text.secondary', ml: { xs: 0, md: 7 }, fontSize: '1.05rem' }}
                >
                  {paragraph}
                </Typography>
              ))}

              {/* Items list */}
              {section.items && (
                <List sx={{ ml: { xs: 0, md: 5 } }}>
                  {section.items.map((item, iIndex) => (
                    <ListItem key={iIndex} sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon sx={{ minWidth: 32, mt: 1 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main', ml: 1 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={item}
                        primaryTypographyProps={{
                          variant: 'body1',
                          sx: { lineHeight: 1.9, color: 'text.secondary', fontSize: '1.05rem' }
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}

              {/* Subsections */}
              {section.subsections && section.subsections.map((subsection, sIndex) => (
                <Box key={sIndex} sx={{ ml: { xs: 0, md: 7 }, mb: 4, mt: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                    {subsection.subtitle}
                  </Typography>
                  <List sx={{ pt: 0 }}>
                    {subsection.items.map((item, iIndex) => (
                      <ListItem key={iIndex} sx={{ py: 0.5, alignItems: 'flex-start' }}>
                        <ListItemIcon sx={{ minWidth: 32, mt: 1 }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main', ml: 1 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={item}
                          primaryTypographyProps={{
                            variant: 'body1',
                            sx: { lineHeight: 1.9, color: 'text.secondary', fontSize: '1.05rem' }
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              ))}

              {/* Note */}
              {section.note && (
                <Box
                  sx={{
                    p: 3,
                    ml: { xs: 0, md: 7 },
                    mt: 3,
                    bgcolor: 'rgba(245, 158, 11, 0.05)',
                    borderLeft: '4px solid #F59E0B',
                    borderRadius: '0 12px 12px 0',
                    border: '1px solid rgba(245, 158, 11, 0.1)',
                    borderLeftWidth: '4px',
                  }}
                >
                  <Typography variant="body1" sx={{ lineHeight: 1.8, color: '#FCD34D' }}>
                    <strong style={{ color: '#F59E0B' }}>Security Note:</strong> {section.note}
                  </Typography>
                </Box>
              )}

              {/* Contact information */}
              {section.contact && (
                <Box sx={{ ml: { xs: 0, md: 7 }, mt: 4 }}>
                  <Box sx={{ p: 4, bgcolor: 'rgba(255,255,255,0.02)', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)' }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, mb: 3, color: 'text.primary' }}>
                      Contact Information
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2, display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                      <span style={{ fontSize: '1.2rem', marginRight: '12px' }}>📧</span>
                      Email: <strong style={{ color: 'white', ml: '8px' }}>{section.contact.email}</strong>
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2, display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                      <span style={{ fontSize: '1.2rem', marginRight: '12px' }}>💬</span>
                      Support: <strong style={{ color: 'white', ml: '8px' }}>{section.contact.support}</strong>
                    </Typography>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                      <span style={{ fontSize: '1.2rem', marginRight: '12px' }}>📍</span>
                      {section.contact.address}
                    </Typography>
                  </Box>
                </Box>
              )}

              {index < sections.length - 1 && <Divider sx={{ mt: 8, borderColor: 'rgba(255,255,255,0.05)' }} />}
            </Box>
          ))}

          {/* Footer */}
          <Box
            sx={{
              mt: 8,
              pt: 6,
              borderTop: '1px solid rgba(255,255,255,0.1)',
              textAlign: 'center'
            }}
          >
            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4, fontWeight: 500 }}>
              By using FastTime, you acknowledge that you have read and understood this Privacy Policy.
            </Typography>
            <Button
              variant="outlined"
              size="large"
              startIcon={<ArrowBack />}
              onClick={() => navigate('/')}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: '12px',
                fontWeight: 600,
                color: 'text.primary',
                borderColor: 'rgba(255,255,255,0.2)',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'rgba(139, 92, 246, 0.05)',
                }
              }}
            >
              Back to Home
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default PrivacyPolicyPage;
